/**
 * /payroll-l3/classification-queue — Pending classification queue.
 *
 * Wired to live endpoints:
 *   GET  /api/payroll-l3/classification-queue
 *   POST /api/payroll-l3/classification-queue/{labor_event_id}/resolve
 *   GET  /api/payroll-l3/cbas/{cba_id}/classifications  (for candidate list)
 */
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import RefreshIcon from "@mui/icons-material/Refresh";

import { api } from "@/lib/api";
import { HelpHint } from "@/components/HelpHint";

export const Route = createFileRoute("/_auth/payroll-l3/classification-queue")({
  component: ClassificationQueuePage,
});

interface QueueItem {
  labor_event_id: string;
  employee_id: string;
  employee_name: string;
  local_id: string;
  work_date: string | null;
  reason: string;
}

interface AISuggestResult {
  labor_event_id: string;
  best_pick: {
    classification_id: string;
    code: string;
    name: string;
    confidence: number | null;
    reasoning: string | null;
  } | null;
  summary: string;
  ai: string;
}

interface Classification {
  id: string;
  title: string;
  trade: string | null;
}

interface Local {
  id: string;
  name: string;
  cbas?: { id: string }[];
}

function reasonChip(r: string) {
  const map: Record<string, { label: string; color: "warning" | "error" | "info" }> = {
    multiple_classifications: { label: "Multiple classifications", color: "warning" },
    multiple_locals: { label: "Multiple locals", color: "warning" },
    no_membership: { label: "No active CBA", color: "error" },
    unknown: { label: "Unknown", color: "info" },
  };
  const c = map[r] ?? { label: r, color: "info" as const };
  return <Chip label={c.label} size="small" color={c.color} />;
}

// ─── Classification picker (loads from active CBA for the local) ──────────────

function ClassificationPicker({
  localId,
  value,
  onChange,
}: {
  localId: string;
  value: string;
  onChange: (v: string) => void;
}) {
  // Find the active CBA for this local, then load its classifications
  const cbasQ = useQuery({
    queryKey: ["payroll-l3", "cbas", { local_id: localId }],
    queryFn: () => api<{ id: string; status: string }[]>(`/api/payroll-l3/cbas?local_id=${localId}`),
    enabled: !!localId,
  });

  const activeCba = cbasQ.data?.find((c) => c.status === "active") ?? cbasQ.data?.[0];

  const classQ = useQuery({
    queryKey: ["payroll-l3", "classifications", activeCba?.id],
    queryFn: () =>
      api<Classification[]>(`/api/payroll-l3/cbas/${activeCba!.id}/classifications`),
    enabled: !!activeCba?.id,
  });

  if (cbasQ.isLoading || classQ.isLoading) {
    return <CircularProgress size={16} />;
  }

  if (!activeCba || !classQ.data?.length) {
    return (
      <TextField
        size="small"
        fullWidth
        label="Classification ID"
        placeholder="Paste UUID"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        sx={{ minWidth: 260 }}
      />
    );
  }

  return (
    <TextField
      select size="small" fullWidth value={value}
      onChange={(e) => onChange(e.target.value)}
      sx={{ minWidth: 260 }}
      label="Classification"
    >
      <MenuItem value="">— select —</MenuItem>
      {classQ.data.map((c) => (
        <MenuItem key={c.id} value={c.id}>
          {c.title}{c.trade ? ` (${c.trade})` : ""}
        </MenuItem>
      ))}
    </TextField>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

function ClassificationQueuePage() {
  const qc = useQueryClient();
  const [selections, setSelections] = useState<Record<string, string>>({});
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [suggestResults, setSuggestResults] = useState<Record<string, AISuggestResult>>({});

  const queueQ = useQuery({
    queryKey: ["payroll-l3", "classification-queue"],
    queryFn: () => api<QueueItem[]>("/api/payroll-l3/classification-queue"),
  });

  const suggestMut = useMutation({
    mutationFn: (labor_event_id: string) =>
      api<AISuggestResult>(
        `/api/payroll-l3/ai/labor-events/${labor_event_id}/classify-suggest`,
        { method: "POST" }
      ),
    onSuccess: (result, labor_event_id) => {
      setSuggestResults((p) => ({ ...p, [labor_event_id]: result }));
      if (result.best_pick) {
        setSelections((p) => ({ ...p, [labor_event_id]: result.best_pick!.classification_id }));
      }
    },
    onError: (e: unknown, labor_event_id) =>
      setRowErrors((p) => ({
        ...p,
        [labor_event_id]: e instanceof Error ? e.message : "AI suggest failed",
      })),
  });

  const assignMut = useMutation({
    mutationFn: ({ labor_event_id, classification_id }: { labor_event_id: string; classification_id: string }) =>
      api(`/api/payroll-l3/classification-queue/${labor_event_id}/resolve`, {
        method: "POST",
        body: { classification_id },
      }),
    onSuccess: (_data, vars) => {
      setRowErrors((p) => { const n = { ...p }; delete n[vars.labor_event_id]; return n; });
      setSelections((p) => { const n = { ...p }; delete n[vars.labor_event_id]; return n; });
      qc.invalidateQueries({ queryKey: ["payroll-l3", "classification-queue"] });
    },
    onError: (e: unknown, vars) =>
      setRowErrors((p) => ({
        ...p,
        [vars.labor_event_id]: e instanceof Error ? e.message : "Failed",
      })),
  });

  const rows = queueQ.data ?? [];

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="h5" fontWeight={600}>Classification Queue</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <HelpHint docPath="payroll-l3/classification.md" label="Classification queue help" />
          <Button
            size="small"
            startIcon={<RefreshIcon />}
            onClick={() => qc.invalidateQueries({ queryKey: ["payroll-l3", "classification-queue"] })}
          >
            Refresh
          </Button>
        </Stack>
      </Stack>

      <Typography variant="body2" color="text.secondary" mb={2}>
        Labor events that couldn't be auto-classified — pick a classification to release them
        into the next pay-run union pass.
      </Typography>

      {queueQ.isLoading && <CircularProgress size={24} />}
      {queueQ.error && <Alert severity="error">Failed to load classification queue.</Alert>}

      {queueQ.data && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Employee</TableCell>
              <TableCell>Work Date</TableCell>
              <TableCell>Local</TableCell>
              <TableCell>Reason</TableCell>
              <TableCell>Assign Classification</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => {
              const value = selections[r.labor_event_id] ?? "";
              const err = rowErrors[r.labor_event_id];
              return (
                <TableRow key={r.labor_event_id} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{r.employee_name}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>
                    {r.work_date ? new Date(r.work_date).toLocaleDateString() : "—"}
                  </TableCell>
                  <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                    {r.local_id.slice(0, 8)}…
                  </TableCell>
                  <TableCell>{reasonChip(r.reason)}</TableCell>
                  <TableCell>
                    <ClassificationPicker
                      localId={r.local_id}
                      value={value}
                      onChange={(v) => setSelections((p) => ({ ...p, [r.labor_event_id]: v }))}
                    />
                    {err && (
                      <Typography variant="caption" color="error" display="block">
                        {err}
                      </Typography>
                    )}
                    {(() => {
                      const sr = suggestResults[r.labor_event_id];
                      if (!sr) return null;
                      if (sr.best_pick) {
                        const conf = sr.best_pick.confidence;
                        return (
                          <Typography variant="caption" color="primary.main" display="block" mt={0.5}>
                            ✦ AI: {sr.best_pick.name}
                            {conf != null ? ` (${Math.round(conf * 100)}% conf.)` : ""}
                          </Typography>
                        );
                      }
                      return (
                        <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                          AI: {sr.summary}
                        </Typography>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5}>
                      <Button
                        size="small"
                        startIcon={<AutoAwesomeIcon fontSize="inherit" />}
                        disabled={suggestMut.isPending}
                        onClick={() => suggestMut.mutate(r.labor_event_id)}
                        title="Ask AI to suggest a classification"
                      >
                        Suggest
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        disabled={!value || assignMut.isPending}
                        onClick={() =>
                          assignMut.mutate({
                            labor_event_id: r.labor_event_id,
                            classification_id: value,
                          })
                        }
                      >
                        Assign
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
            {rows.length === 0 && !queueQ.isLoading && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ color: "text.secondary", py: 4 }}>
                  Queue is empty — every labor event resolved cleanly.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}
